import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function EventForm({ onCreated, lightMode, initialData }) {
  const [form, setForm] = useState({ 
    name: '', 
    location: '', 
    date: '', 
    status: 0, 
    CityId: '',
    startTime: '',
    endTime: '',
    description: '',
    coverImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [availableTicketTypes, setAvailableTicketTypes] = useState([]);
  const [eventTickets, setEventTickets] = useState([]);

  useEffect(() => {
    fetchCities();
    fetchTicketTypes();
  }, []);

  const fetchCities = async () => {
    try {
      const res = await axios.get('/speeddate/api/cities');
      setCities(res.data);
    } catch (err) {
      console.error("Error fetching cities", err);
    }
  };

  const fetchTicketTypes = async () => {
    try {
      const res = await axios.get('/speeddate/api/tickets');
      setAvailableTicketTypes(res.data);
    } catch (err) {
      console.error("Error fetching ticket types", err);
    }
  };

  useEffect(() => {
    if (initialData) {
      const formattedDate = initialData.date ? new Date(initialData.date).toISOString().split('T')[0] : '';
      setForm({
        name: initialData.name || '',
        location: initialData.location || '',
        date: formattedDate,
        status: initialData.status !== undefined ? initialData.status : 0,
        CityId: initialData.CityId || '',
        startTime: initialData.startTime || '',
        endTime: initialData.endTime || '',
        description: initialData.description || '',
        coverImage: initialData.coverImage || ''
      });
      // Set existing ticket types for this event
      if (initialData.EventTicketTypes) {
        setEventTickets(initialData.EventTicketTypes.map(et => ({
          TicketId: et.TicketId,
          price: et.price
        })));
      } else {
        setEventTickets([]);
      }
    } else {
      setForm({ 
        name: '', 
        location: '', 
        date: '', 
        status: 0, 
        CityId: '',
        startTime: '',
        endTime: '',
        description: '',
        coverImage: ''
      });
      setEventTickets([]);
    }
  }, [initialData]);

  const handleToggleTicket = (ticketId) => {
    setEventTickets(prev => {
      const exists = prev.find(et => et.TicketId === ticketId);
      if (exists) {
        return prev.filter(et => et.TicketId !== ticketId);
      } else {
        return [...prev, { TicketId: ticketId, price: 0 }];
      }
    });
  };

  const handlePriceChange = (ticketId, price) => {
    setEventTickets(prev => prev.map(et => 
      et.TicketId === ticketId ? { ...et, price: parseFloat(price) || 0 } : et
    ));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, coverImage: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...form, eventTickets };
      if (initialData && initialData.id) {
        await axios.put(`/speeddate/api/events/${initialData.id}`, payload);
        window.Swal.fire({
          title: 'Evento Aggiornato!',
          text: 'Le modifiche sono state salvate.',
          icon: 'success',
          confirmButtonColor: '#3b82f6'
        });
      } else {
        await axios.post('/speeddate/api/events', payload);
        window.Swal.fire({
          title: 'Evento Creato!',
          text: 'L\'evento è stato aggiunto con successo.',
          icon: 'success',
          confirmButtonColor: '#3b82f6'
        });
        setForm({ 
          name: '', 
          location: '', 
          date: '', 
          status: 0, 
          CityId: '',
          startTime: '',
          endTime: '',
          description: '',
          coverImage: ''
        });
        setEventTickets([]);
      }
      if (onCreated) onCreated();
    } catch (err) {
      window.Swal.fire('Errore', "Si è verificato un problema.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `form-control ${lightMode ? 'bg-white bg-opacity-10 text-white border-0 shadow-none placeholder-white-50' : ''}`;
  const selectClass = `form-select ${lightMode ? 'bg-white bg-opacity-10 text-white border-0 shadow-none' : ''}`;
  const labelClass = `form-label small fw-bold ${lightMode ? 'text-white text-opacity-75' : 'text-muted'}`;

  const isLocked = initialData && initialData.status > 3;

  return (
    <form onSubmit={submit}>
      {isLocked && (
        <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.8rem' }}>
          <i className="fas fa-lock me-2"></i>
          Evento bloccato perché in corso o terminato.
        </div>
      )}
      <div className="mb-3">
        <label className={labelClass}>Titolo Evento</label>
        <input
          type="text"
          className={inputClass}
          placeholder="es. Gran Galà SpeedDate"
          required
          disabled={isLocked}
          style={lightMode ? { colorScheme: 'dark' } : {}}
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div className="mb-3">
        <label className={labelClass}>Locale / Indirizzo</label>
        <input
          type="text"
          className={inputClass}
          placeholder="es. Virtual Bar, Roma"
          required
          disabled={isLocked}
          value={form.location}
          onChange={e => setForm({ ...form, location: e.target.value })}
        />
      </div>
      <div className="mb-3">
        <label className={labelClass}>Città</label>
        <select
          className={selectClass}
          required
          disabled={isLocked}
          value={form.CityId}
          onChange={e => setForm({ ...form, CityId: e.target.value })}
          style={lightMode ? { colorScheme: 'dark' } : {}}
        >
          <option value="" disabled>Seleziona Città...</option>
          {cities.map(city => (
            <option key={city.id} value={city.id} style={{ color: '#000' }}>
              {city.name} {city.province ? `(${city.province})` : ''}
            </option>
          ))}
        </select>
      </div>
      
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className={labelClass}>Data</label>
          <input
            type="date"
            className={inputClass}
            required
            disabled={isLocked}
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            style={lightMode ? { colorScheme: 'dark' } : {}}
          />
        </div>
        <div className="col-md-3">
          <label className={labelClass}>Inizio</label>
          <input
            type="time"
            className={inputClass}
            disabled={isLocked}
            value={form.startTime}
            onChange={e => setForm({ ...form, startTime: e.target.value })}
            style={lightMode ? { colorScheme: 'dark' } : {}}
          />
        </div>
        <div className="col-md-3">
          <label className={labelClass}>Fine</label>
          <input
            type="time"
            className={inputClass}
            disabled={isLocked}
            value={form.endTime}
            onChange={e => setForm({ ...form, endTime: e.target.value })}
            style={lightMode ? { colorScheme: 'dark' } : {}}
          />
        </div>
      </div>

      <div className="card border-0 bg-light mb-4 shadow-sm overflow-hidden">
        <div className="card-header bg-dark text-white py-2">
            <h6 className="mb-0 small fw-bold">Gestione Biglietti Evento</h6>
        </div>
        <div className="card-body p-3">
            <div className="row g-3">
                {availableTicketTypes.map(tt => {
                    const selected = eventTickets.find(et => et.TicketId === tt.id);
                    return (
                        <div key={tt.id} className="col-12 col-md-6">
                            <div className={`p-2 rounded border ${selected ? 'border-primary bg-white shadow-sm' : 'border-secondary opacity-75'}`}>
                                <div className="form-check d-flex align-items-center mb-1">
                                    <input 
                                        className="form-check-input me-2" 
                                        type="checkbox" 
                                        id={`tt-${tt.id}`}
                                        checked={!!selected}
                                        onChange={() => handleToggleTicket(tt.id)}
                                        disabled={isLocked}
                                    />
                                    <label className="form-check-label fw-bold small flex-grow-1" htmlFor={`tt-${tt.id}`}>
                                        {tt.name}
                                    </label>
                                </div>
                                {selected && (
                                    <div className="input-group input-group-sm mt-1">
                                        <span className="input-group-text bg-light border-0 small">€</span>
                                        <input 
                                            type="number" 
                                            step="0.01"
                                            className="form-control border-0 bg-light"
                                            placeholder="Prezzo"
                                            value={selected.price}
                                            onChange={(e) => handlePriceChange(tt.id, e.target.value)}
                                            disabled={isLocked}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {availableTicketTypes.length === 0 && (
                <div className="text-center text-muted small py-3">
                    Nessun tipo di biglietto disponibile nell'anagrafica.
                </div>
            )}
        </div>
      </div>

      <div className="mb-4">
        <label className={labelClass}>Descrizione HTML (Copia/Incolla)</label>
        <textarea
          className={inputClass}
          rows="4"
          placeholder="Incolla qui il codice HTML della descrizione..."
          disabled={isLocked}
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
        ></textarea>
      </div>

      <div className="mb-4">
        <label className={labelClass}>Immagine di Copertina</label>
        <div className="d-flex align-items-start gap-3">
          {form.coverImage && (
            <div className="rounded overflow-hidden shadow-sm" style={{ width: '80px', height: '80px', flexShrink: 0 }}>
              <img src={form.coverImage} alt="Cover Preview" className="w-100 h-100 object-fit-cover" />
            </div>
          )}
          <div className="flex-grow-1">
            <input
              type="file"
              accept="image/*"
              className={inputClass}
              disabled={isLocked}
              onChange={handleFileChange}
            />
            <div className="text-muted small mt-1">Carica l'immagine invariata (massimo 2MB consigliati)</div>
          </div>
        </div>
      </div>

      <div className="d-grid pt-2">
        <button type="submit" className={`btn ${lightMode ? 'btn-white text-primary fw-bold' : 'btn-primary'} shadow-sm`} disabled={loading || isLocked}>
          {loading ? 'Attendere...' : (initialData ? 'Aggiorna Evento' : 'Crea Evento')}
        </button>
      </div>
    </form>
  );
}
